import { DateTime } from "luxon";

import { SessionContextProps } from "../../../Context/SessionContext";
import { UserContextProps } from "../../../Context/UserContext";
import { ImprovementAreas, ImprovementAreasMap } from "../../../Models/Customer";
import { LeadExternalSystemMap } from "../../../Utils/Enums";
import { INoteTagConfiguration } from "./NoteTagsConfiguration";

const didCustomerQiChange = ({
  customer,
  selectedCustomer
}: Pick<SessionContextProps, "customer" | "selectedCustomer">): boolean =>
  Boolean(
    JSON.stringify(selectedCustomer?.improvementAreas) !== JSON.stringify(customer.improvementAreas) ||
      selectedCustomer?.height?.feet !== customer.height?.feet ||
      selectedCustomer?.height?.inches !== customer.height?.inches ||
      selectedCustomer?.weight !== customer.weight ||
      selectedCustomer?.dateOfBirth !== customer.dateOfBirth
  );

/** Tag the appointment's service sub-category, if one was created. */
const noLabelAppointmentServiceSubCategory: INoteTagConfiguration<Pick<SessionContextProps, "appointment">> = {
  label: null,
  template: ({ appointment }) => appointment?.serviceSubCategory || null
};

/** Tag the initials of the patient ambassador user who authored the session. */
const noLabelPatientAmbassadorInitials: INoteTagConfiguration<Pick<UserContextProps, "user">> = {
  label: null,
  template: ({ user }) =>
    Boolean(user.firstName) && Boolean(user.lastName)
      ? user.firstName[0].toUpperCase() + user.lastName[0].toUpperCase()
      : user.username.substring(0, 2).toUpperCase()
};

/** Tag the name of the lead's external system. */
const noLabelLeadContactType: INoteTagConfiguration<Pick<SessionContextProps, "lead">> = {
  label: null,
  template: ({ lead }) => LeadExternalSystemMap[lead.externalSystem]
};

/** Show the appointment business unit, else the lead campaign's business unit. */
const noLabelServiceType: INoteTagConfiguration<Pick<SessionContextProps, "appointment" | "lead">> = {
  label: null,
  template: ({ appointment, lead }) => appointment?.businessUnit || lead.campaign.businessUnit.name || null
};

/** Shows all selected customer areas of interest. */
const interests: INoteTagConfiguration<Pick<SessionContextProps, "customer">> = {
  label: null,
  template: ({ customer }) => {
    const interests = Object.entries(customer.improvementAreas)
      .filter(([, value]) => value)
      .map(([key]) => ImprovementAreasMap[key as keyof ImprovementAreas]);
    return interests.length ? interests.reduce((prev, current) => `${prev}, ${current}`) : null;
  }
};

/** Shows all areas of interests tag only if an appointment was created and QI changed. */
const interestsIfAppointmentAndQiChanged: INoteTagConfiguration<
  Pick<SessionContextProps, "appointment" | "customer" | "selectedCustomer">
> = {
  label: null,
  template: ({ appointment, customer, selectedCustomer }) => {
    if (!appointment?.id && !didCustomerQiChange({ customer, selectedCustomer })) return null;
    return interests.template({ customer });
  }
};

/** Tag the known values for the customer's height, weight, and BMI. */
const customerHeightWeightBmi: INoteTagConfiguration<Pick<SessionContextProps, "customer">> = {
  label: "Height/Weight/Bmi",
  template: ({ customer }) => {
    const parts: string[] = [];
    if (customer.height) parts.push(`${customer.height.feet}ft${customer.height.inches}in`);
    if (customer.weight) parts.push(`${customer.weight}lbs`);
    if (customer.bmi) parts.push(`${customer.bmi}BMI`);
    return parts.length ? parts.reduce((prev, current) => `${prev}, ${current}`) : null;
  }
};

/** Tag the customer's date of birth if set. */
const customerDateOfBirth: INoteTagConfiguration<Pick<SessionContextProps, "customer">> = {
  label: "DOB",
  template: ({ customer }) =>
    customer.dateOfBirth ? DateTime.fromISO(customer.dateOfBirth).toLocaleString(DateTime.DATE_MED) : null
};

/** If the appointment exists and the customer's qualifying information has changed, show the customer's height,
 * weight, and BMI.
 */
const customerQualificationInfoIfAppointmentAndQiChanged: INoteTagConfiguration<
  Pick<SessionContextProps, "appointment" | "customer" | "selectedCustomer">
> = {
  label: "QI",
  template: ({ appointment, customer, selectedCustomer }) => {
    if (!appointment?.id && !didCustomerQiChange({ customer, selectedCustomer })) return null;

    const parts: string[] = [];
    const customerHeightWeightBmiTag = customerHeightWeightBmi.template({ customer });
    if (customerHeightWeightBmiTag) parts.push(customerHeightWeightBmiTag);
    const dob = customerDateOfBirth.template({ customer });
    if (dob) parts.push(dob);
    return parts.length ? parts.reduce((prev, current) => `${prev}, ${current}`) : null;
  }
};

/** Tag the customer's center and distance to center if set. */
const centerAndDistance: INoteTagConfiguration<Pick<SessionContextProps, "customer">> = {
  label: "Center",
  template: ({ customer }) =>
    `${customer.distanceToCenter ? `${customer.distanceToCenter} mi` : ""}${
      customer?.center?.name && customer.distanceToCenter ? " / " : ""
    }${customer?.center?.name || ""}` || null
};

/** If changed from a previous session, tag the customer's center and distance to center. */
const centerAndDistanceIfChanged: INoteTagConfiguration<Pick<SessionContextProps, "customer" | "selectedCustomer">> = {
  label: centerAndDistance.label,
  template: ({ customer, selectedCustomer }) =>
    (customer?.center?.name && selectedCustomer?.centerId !== customer.centerId) ||
    (customer.distanceToCenter && selectedCustomer?.distanceToCenter !== customer.distanceToCenter)
      ? centerAndDistance.template({ customer })
      : null
};

/** Tag non-candidate status if set. */
const ignorableNonCanStatus: INoteTagConfiguration<Pick<SessionContextProps, "ignoreNonCan" | "leadDisqualifiers">> = {
  label: "Non-Can",
  template: ({ ignoreNonCan, leadDisqualifiers }) => {
    if (ignoreNonCan) return null;
    const nonCans = leadDisqualifiers;
    return nonCans.length
      ? `Non-Can: ${nonCans.reduce((prev, { name }) => (prev ? `${prev}, ${name}` : name), "")}`
      : null;
  }
};

/** Tag diabetic customers if set. */
const customerDiabetic: INoteTagConfiguration<Pick<SessionContextProps, "medicalConclusions">> = {
  label: "Diabetic",
  template: ({ medicalConclusions: { diabetic } }) => (diabetic ? "Diabetic" : null)
};

/** Tag the customer as having radiation area restrictions if set. */
const customerRadiationArea: INoteTagConfiguration<Pick<SessionContextProps, "medicalConclusions">> = {
  label: "Radiation",
  template: ({ medicalConclusions: { hasRadiationAreaRestrictions } }) =>
    hasRadiationAreaRestrictions ? `Radiation area restriction` : null
};

/** Tag the customer as requiring a defibrillator evaluation if they have a non-defibrillator pacemaker. */
const defibrillatorEvalRequired: INoteTagConfiguration<Pick<SessionContextProps, "medicalConclusions">> = {
  label: "Defibrillator",
  template: ({ medicalConclusions: { hasNonDefibrillatorPacemaker } }) =>
    hasNonDefibrillatorPacemaker ? `Pacemaker evaluation at consult` : null
};

/** All of the note tag configurations available to the application for customer or appointment use. */
const NoteTagConfigs = {
  centerAndDistance,
  centerAndDistanceIfChanged,
  customerDiabetic,
  customerDateOfBirth,
  customerHeightWeightBmi,
  customerQualificationInfoIfAppointmentAndQiChanged,
  interests,
  interestsIfAppointmentAndQiChanged,
  noLabelAppointmentServiceSubCategory,
  noLabelLeadContactType,
  noLabelPatientAmbassadorInitials,
  noLabelServiceType,
  ignorableNonCanStatus: ignorableNonCanStatus,
  customerRadiationArea,
  defibrillatorEvalRequired
};

export default NoteTagConfigs;
